import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function Story() {
  useEffect(() => {
    document.title = 'Our Story - Salt & Stone';
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="story-page">
      {/* Hero */}
      <section className="py-16 md:py-24 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <span className="caption block mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Our Story
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F2F0E9] mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Where Sea Meets Mountain
          </h1>
          <div className="w-20 h-px bg-[#D4AF37] mx-auto mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}></div>
          <p className="text-[#F2F0E9]/70 text-lg max-w-2xl mx-auto leading-relaxed animate-fade-in opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
            The story of a small island with infinite contrasts, and the spirit that captures its essence.
          </p>
        </div>
      </section>

      {/* Full-width Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/bqovt6r2_sea-rocks-cyprus-travel-vacation-218c40-1024.webp"
          alt="Cyprus Coastline - Salt and Stone"
          className="w-full h-full object-cover brightness-[0.6] saturate-[0.6] contrast-[1.1]"
        />
        <div className="absolute inset-0 bg-[#050505]/30 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60"></div>
      </section>

      {/* The Island */}
      <section className="py-24 md:py-32 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <span className="caption block mb-4">The Island</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-6">
                Cyprus
              </h2>
              <div className="w-12 h-px bg-[#D4AF37] mb-8"></div>
              <p className="text-[#F2F0E9]/70 mb-6 leading-relaxed">
                Cyprus stands at the crossroads of three continents — a Mediterranean jewel where ancient civilizations left their mark and nature crafted landscapes of extraordinary beauty.
              </p>
              <p className="text-[#F2F0E9]/70 leading-relaxed">
                Here, you can ski in the morning and swim in the afternoon. Snow-capped peaks give way to sun-drenched coasts. Wild forests meet quiet shores. It's a place of rare contrasts.
              </p>
            </div>
            <div className="lg:col-span-7 story-image aspect-[4/3] overflow-hidden relative">
              <img
                src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/nz181u4a_Adonis-Trail-5e9e8d60.webp"
                alt="Cyprus Coastline - Adonis Trail"
                className="w-full h-full object-cover brightness-[0.65] saturate-[0.6] contrast-[1.1]"
              />
              <div className="absolute inset-0 bg-[#050505]/25 mix-blend-multiply"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/50 via-transparent to-[#050505]/30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Botanicals */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1 story-image overflow-hidden relative flex items-center justify-center bg-[#0A0A0A]">
              <img
                src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/vlal1sz6_9510D171-6D9A-4625-9FFD-CB95D765E1BE.PNG"
                alt="Salt & Stone Gin with Botanicals"
                className="w-full h-auto object-contain brightness-[0.7] saturate-[0.7] contrast-[1.1]"
              />
              <div className="absolute inset-0 bg-[#050505]/20 mix-blend-multiply"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-[#0A0A0A]/30"></div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <span className="caption block mb-4">The Craft</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-6">
                30 Botanicals
              </h2>
              <div className="w-12 h-px bg-[#D4AF37] mb-8"></div>
              <p className="text-[#F2F0E9]/70 mb-6 leading-relaxed">
                We source thirty unique botanicals from across Cyprus — from the aromatic herbs of the Troodos mountains to the wild plants that thrive along the Mediterranean coast.
              </p>
              <p className="text-[#F2F0E9]/70 leading-relaxed">
                Each botanical is carefully selected for its unique contribution to our flavour profile. Together, they create a gin that is complex yet harmonious, bold yet refined.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-24 md:py-32 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mb-12"></div>
          <blockquote className="quote mb-12">
            "Thirty botanicals from coasts and peaks unite in harmony, crafting a flavour both vivid and refined."
          </blockquote>
          <div className="w-12 h-px bg-[#D4AF37] mx-auto"></div>
        </div>
      </section>

      {/* The Spirit */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <span className="caption block mb-4">The Spirit</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-6">
                Unfiltered Excellence
              </h2>
              <div className="w-12 h-px bg-[#D4AF37] mb-8"></div>
              <p className="text-[#F2F0E9]/70 mb-6 leading-relaxed">
                Salt & Stone Gin is left unfiltered — a deliberate choice that preserves the rich natural oils released during distillation. This gives our gin its distinctive texture and intense aromatic character.
              </p>
              <p className="text-[#F2F0E9]/70 mb-8 leading-relaxed">
                The result is a spirit that speaks of its origins — Mediterranean warmth, mountain freshness, and the ancient craft traditions of Cyprus.
              </p>
              <div className="flex flex-wrap gap-4">
                <span className="badge-gold">43% ABV</span>
                <span className="badge-gold">700ML</span>
                <span className="badge-gold">Small Batch</span>
              </div>
            </div>
            <div className="lg:col-span-7 story-image aspect-[5/6] overflow-hidden relative">
              <img
                src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/e6vbc2gy_FCD7C585-B3E8-495B-B109-65D48E4CD43B.PNG"
                alt="Salt & Stone Gin Premium Packaging"
                className="w-full h-full object-cover brightness-[0.7] saturate-[0.7] contrast-[1.1]"
              />
              <div className="absolute inset-0 bg-[#050505]/20 mix-blend-multiply"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-[#0A0A0A]/30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 md:py-32 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <span className="caption block mb-6">Our Philosophy</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-8">
            Balance in Contrast
          </h2>
          <div className="w-20 h-px bg-[#D4AF37] mx-auto mb-12"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">Salt</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                The mineral essence of sea-swept coasts. The foundation of life and flavour.
              </p>
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">&</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Unity. Harmony. The meeting point where opposites become one.
              </p>
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">Stone</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                The enduring strength of mountains. Stability. Time immemorial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-8">
            Experience the Spirit of Cyprus
          </h2>
          <p className="text-[#F2F0E9]/70 mb-12 max-w-xl mx-auto">
            Discover Salt & Stone Gin — where thirty botanicals tell the story of an island like no other.
          </p>
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="story-shop-btn">
            Shop Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
